import React, { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import { apiGet } from '../../utils/api';
import { useTeamChat } from '../../hooks/useTeamChat';
import ConversationList from './ConversationList';
import ConversationView from './ConversationView';

export default function TeamChatPage() {
    const { theme } = useTheme();
    const { currentUser } = useAuth();
    const [conversations, setConversations] = useState([]);
    const [activeConversation, setActiveConversation] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showMobileList, setShowMobileList] = useState(true);
    const [sidebarOpen, setSidebarOpen] = useState(true);
    const [latestEvent, setLatestEvent] = useState(null);

    const handleEvent = useCallback((event) => {
        setLatestEvent(event);

        if (event.type === 'new_message') {
            const isOwnMessage = event.message?.user?.id === currentUser?.id;
            const isActiveConversation = event.conversation_id === activeConversation?.id;

            setConversations(prev => {
                const updated = prev.map(c => {
                    if (c.id === event.conversation_id) {
                        return {
                            ...c,
                            last_message: {
                                content: event.message.content,
                                user: event.message.user,
                                created_at: event.message.created_at
                            },
                            last_message_at: event.message.created_at,
                            unread_count: (isOwnMessage || isActiveConversation) ? c.unread_count : (c.unread_count || 0) + 1
                        };
                    }
                    return c;
                });
                return updated.sort((a, b) =>
                    new Date(b.last_message_at || 0) - new Date(a.last_message_at || 0)
                );
            });
        }

        if (event.type === 'conversation_updated') {
            setConversations(prev => {
                const exists = prev.find(c => c.id === event.conversation?.id);
                if (exists) {
                    return prev.map(c => c.id === event.conversation.id ? { ...c, ...event.conversation } : c);
                }
                return [event.conversation, ...prev];
            });
        }

        if (event.type === 'member_removed' && event.user_id === currentUser?.id) {
            setConversations(prev => prev.filter(c => c.id !== event.conversation_id));
            if (activeConversation?.id === event.conversation_id) {
                setActiveConversation(null);
            }
        }

        if (event.type === 'member_invited' || event.type === 'invite_accepted' || event.type === 'invite_declined') {
            apiGet('/conversations').then(async (response) => {
                if (response.ok) {
                    const data = await response.json();
                    setConversations(data.conversations || []);
                }
            }).catch(() => {});
        }
    }, [currentUser?.id, activeConversation?.id]);

    const { isConnected, sendTyping, sendStoppedTyping, markRead } = useTeamChat(handleEvent);

    useEffect(() => {
        const fetchConversations = async () => {
            setLoading(true);
            try {
                const response = await apiGet('/conversations');
                if (response.ok) {
                    const data = await response.json();
                    setConversations(data.conversations || []);
                }
            } catch (err) {
                console.error('Failed to fetch conversations:', err);
            } finally {
                setLoading(false);
            }
        };
        fetchConversations();
    }, []);

    const handleSelectConversation = (conversation) => {
        setActiveConversation(conversation);
        setShowMobileList(false);

        setConversations(prev =>
            prev.map(c => c.id === conversation.id ? { ...c, unread_count: 0 } : c)
        );

        markRead?.(conversation.id);
    };

    const handleConversationUpdated = (updatedConversation) => {
        setConversations(prev =>
            prev.map(c => c.id === updatedConversation.id ? { ...c, ...updatedConversation } : c)
        );
        if (activeConversation?.id === updatedConversation.id) {
            setActiveConversation(prev => ({ ...prev, ...updatedConversation }));
        }
    };

    if (loading) {
        return (
            <div className={`flex items-center justify-center h-full ${
                theme === 'dark' ? 'bg-gray-900' : 'bg-gray-50'
            }`}>
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
            </div>
        );
    }

    return (
        <div className={`flex h-full overflow-hidden ${
            theme === 'dark' ? 'bg-gray-900' : 'bg-gray-50'
        }`}>
            {/* Sidebar — animated slide like EmailDashboard */}
            <div className={`${
                showMobileList ? 'flex' : 'hidden'
            } md:flex flex-shrink-0 overflow-hidden transition-all duration-300 ease-in-out ${
                sidebarOpen ? 'w-full md:w-64' : 'md:w-0'
            }`}>
                <div className="w-full md:w-64 h-full flex-shrink-0">
                    <ConversationList
                        conversations={conversations}
                        activeConversationId={activeConversation?.id}
                        onSelectConversation={handleSelectConversation}
                        onConversationsUpdated={setConversations}
                    />
                </div>
            </div>

            {/* Sidebar toggle — visible when collapsed on desktop */}
            {!sidebarOpen && (
                <button
                    onClick={() => setSidebarOpen(true)}
                    className={`hidden md:flex absolute left-0 top-1/2 -translate-y-1/2 z-10 p-1.5 rounded-r-lg border border-l-0 transition-colors ${
                        theme === 'dark'
                            ? 'bg-gray-800 border-gray-700 hover:bg-gray-700 text-gray-400'
                            : 'bg-white border-gray-200 hover:bg-gray-50 text-gray-500'
                    }`}
                    title="Show conversations"
                >
                    <ChevronRight size={16} />
                </button>
            )}

            {/* Main chat area */}
            <div className={`${
                showMobileList ? 'hidden' : 'flex'
            } md:flex flex-1 min-w-0 relative`}>
                <ConversationView
                    conversation={activeConversation}
                    onConversationUpdated={handleConversationUpdated}
                    onBack={() => setShowMobileList(true)}
                    teamChatEvents={latestEvent}
                    sendTyping={sendTyping}
                    sendStoppedTyping={sendStoppedTyping}
                    markRead={markRead}
                    sidebarOpen={sidebarOpen}
                    onToggleSidebar={() => setSidebarOpen(!sidebarOpen)}
                />
            </div>
        </div>
    );
}
